import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDownIcon, EllipsisHorizontalIcon } from "@heroicons/react/24/outline";

/**
 * A row "…" menu following the WAI-ARIA menu-button pattern: Enter / Space / ArrowDown on
 * the trigger opens it and focuses the first item; ArrowUp / ArrowDown / Home / End move;
 * Escape closes and returns focus to the trigger; Tab or a click outside closes.
 *
 * The menu is `position: fixed` at the trigger, so a table's `overflow-x-auto` wrapper
 * cannot clip it. It closes on scroll / resize rather than chasing the trigger.
 */
export interface ActionItem {
  label: string;
  onSelect: () => void;
  tone?: "default" | "success" | "danger" | "muted";
  disabled?: boolean;
  title?: string;
  /** Draw a divider above this item. */
  dividerBefore?: boolean;
}

interface Props {
  /** Accessible name of the trigger, e.g. "Actions for station-x". */
  label: string;
  items: ActionItem[];
  /** Optional visible text next to the icon. */
  buttonText?: string;
  /** "kebab" (row actions, "…") or "dropdown" (a labelled button with ▾). */
  variant?: "kebab" | "dropdown";
}

const TONE: Record<NonNullable<ActionItem["tone"]>, string> = {
  default: "text-gray-100",
  success: "text-green-300",
  danger: "text-red-300",
  muted: "text-gray-400",
};

const ActionsMenu: React.FC<Props> = ({ label, items, buttonText, variant = "kebab" }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const focusItem = (i: number) => itemRefs.current[i]?.focus();

  const openMenu = (focus: "first" | "last") => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
    // Focus after the menu renders.
    setTimeout(() => {
      if (!enabled.length) return;
      focusItem(focus === "first" ? enabled[0] : enabled[enabled.length - 1]);
    }, 0);
  };

  // Flip above the trigger when the menu would run off the bottom of the viewport.
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !triggerRef.current || !pos) return;
    const h = menuRef.current.offsetHeight;
    const r = triggerRef.current.getBoundingClientRect();
    if (r.bottom + 4 + h > window.innerHeight && r.top - 4 - h > 0) {
      setPos((p) => (p ? { ...p, top: r.top - 4 - h } : p));
    }
    // Only once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close(false);
    };
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      close(false);
    };
    const onResize = () => close(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, close]);

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openMenu("first");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openMenu("last");
    }
  };

  const onMenuKey = (e: React.KeyboardEvent) => {
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    const at = enabled.indexOf(current);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusItem(enabled[(at + 1) % enabled.length]);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusItem(enabled[(at - 1 + enabled.length) % enabled.length]);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusItem(enabled[0]);
    } else if (e.key === "End") {
      e.preventDefault();
      focusItem(enabled[enabled.length - 1]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          if (open) close(false);
          else openMenu("first");
        }}
        onKeyDown={onTriggerKey}
        className="inline-flex items-center gap-1 rounded-md border border-gray-700 bg-gray-800 px-2 py-1 text-sm text-gray-200 hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
      >
        {buttonText && <span>{buttonText}</span>}
        {variant === "dropdown" ? (
          <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
        ) : (
          <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          onClick={(e) => e.stopPropagation()}
          style={pos ? { top: pos.top, right: pos.right } : undefined}
          className="fixed z-50 min-w-[11rem] rounded-md border border-gray-700 bg-gray-800 py-1 text-left shadow-xl"
        >
          {items.map((it, i) => (
            <React.Fragment key={it.label}>
              {it.dividerBefore && <div role="separator" className="my-1 border-t border-gray-700" />}
              <button
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={it.disabled}
                title={it.title}
                onClick={() => {
                  close(true);
                  it.onSelect();
                }}
                className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-700 focus:bg-gray-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
                  TONE[it.tone ?? "default"]
                }`}
              >
                {it.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
};

export default ActionsMenu;
